import { AxiosInstance, AxiosResponse } from "axios"
import type {
  AssistantStatus,
  AssistantTurnRequest,
  AssistantTurnResponse,
} from "features/Assistant/types"

/**
 * Sends one assistant turn. Always async-capable: the Conductor answers 202
 * with a job id when the turn takes longer than the `ASync-Request` wait, and
 * the caller resolves it with `useAsyncCapable().awaitAsyncCapable`.
 *
 * The body is not trusted here: a failure can come back as plain text or
 * HTML, so the caller checks it is an `AssistantTurnResponse`.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param req - The turn: conversation, screen and its context
 * @returns The 200 (the response) or 202 (the job id) response
 */
export async function postAssistantTurn(
  axiosInstance: AxiosInstance,
  req: AssistantTurnRequest
): Promise<AxiosResponse<AssistantTurnResponse | string>> {
  const response = await axiosInstance.post<AssistantTurnResponse | string>(
    "/assistant-turn",
    req,
    { headers: { "ASync-Request": 10 } }
  )
  return response
}

/**
 * Whether the user's organisation has the assistant switched on (the Kinde
 * organisation property `ai_assistant`).
 *
 * @param axiosInstance - Authenticated axios instance
 * @returns `{ enabled }`, or the raw body if it is not JSON
 */
export async function getAssistantStatus(
  axiosInstance: AxiosInstance
): Promise<AxiosResponse<AssistantStatus | string>> {
  const response = await axiosInstance.get<AssistantStatus | string>(
    "/assistant-status"
  )
  return response
}
